import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Heart, Users } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// Scoped palette (matches LandingPage)
const lp = {
  midnightNavy: '#1C2033',
  iconPurple: 'var(--color-primary, #5F3DC4)',
  softLavender: 'var(--color-primary-light, #BCAEE6)',
  offWhite: '#F4F3F8',
  mist: '#E8E6F0',
  mutedSlate: '#6B6F85',
  white: '#FFFFFF',
  ctaGradient: 'linear-gradient(135deg, var(--color-primary, #5F3DC4), var(--color-primary-light, #BCAEE6))',
};

const dmSansLinkId = 'about-page-dm-sans';

function injectFonts() {
  if (document.getElementById(dmSansLinkId)) return;
  const link = document.createElement('link');
  link.id = dmSansLinkId;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

function cleanupFonts() {
  document.getElementById(dmSansLinkId)?.remove();
}

const RESPONSIVE_CSS = `
  @media (max-width: 768px) {
    .ap-hero-headline {
      font-size: 36px !important;
    }
    .ap-section-title {
      font-size: 24px !important;
    }
    .ap-values-grid,
    .ap-contact-grid {
      grid-template-columns: 1fr !important;
    }
    .ap-section {
      padding: 48px 0 !important;
    }
  }
`;

export function AboutPage() {
  useDocumentTitle('About');

  useEffect(() => {
    injectFonts();
    return () => cleanupFonts();
  }, []);

  return (
    <div style={styles.page}>
      <style>{RESPONSIVE_CSS}</style>

      {/* Nav — logo only, matches LandingPage */}
      <nav style={styles.nav}>
        <div style={styles.navContainer}>
          <Link to="/" style={styles.navLogoLink}>
            <img src="/img/snagg_assets/snagg_combined_color.svg" alt="snagg" style={styles.navLogoImg} />
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <span style={styles.eyebrowPill}>About</span>
          <h1 className="ap-hero-headline" style={styles.heroHeadline}>
            Shopping,{' '}
            <span style={styles.heroHeadlineAccent}>live</span>.
            <br />
            Not listed.
          </h1>
          <p style={styles.heroSub}>
            We're rebuilding resale commerce around live video — built by sellers, for sellers and the
            buyers who love them.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="ap-section" style={styles.section}>
        <div style={styles.sectionInner}>
          <h2 className="ap-section-title" style={styles.sectionTitle}>Our mission</h2>
          <p style={styles.sectionBody}>
            The best way to buy and sell sneakers, trading cards, vintage apparel, and collectibles
            isn't a listing — it's a conversation. Snagg gives sellers the tools to run professional
            live shows and gives buyers a trustworthy, entertaining way to find pieces they love.
          </p>
        </div>
      </section>

      {/* Why we built Snagg */}
      <section className="ap-section" style={{ ...styles.section, backgroundColor: lp.offWhite }}>
        <div style={styles.sectionInner}>
          <h2 className="ap-section-title" style={styles.sectionTitle}>Why we built Snagg</h2>
          <p style={styles.sectionBody}>
            We've been operating as sellers on other marketplaces for years. We wanted to bring costs
            down for sellers — without giving up the first-class experience buyers expect. Snagg is
            the platform we wished we had.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="ap-section" style={styles.section}>
        <div style={styles.sectionInner}>
          <h2 className="ap-section-title" style={styles.sectionTitle}>What we believe</h2>
          <div className="ap-values-grid" style={styles.valuesGrid}>
            <div style={styles.valueCard}>
              <div style={styles.valueIcon}><DollarSign size={20} color="#fff" strokeWidth={2.25} /></div>
              <h3 style={styles.valueTitle}>Some of the lowest fees in the industry</h3>
              <p style={styles.valueDesc}>
                Sellers keep more of what they earn. Transparent pricing, no surprise line items.
              </p>
            </div>
            <div style={styles.valueCard}>
              <div style={styles.valueIcon}><Heart size={20} color="#fff" strokeWidth={2.25} /></div>
              <h3 style={styles.valueTitle}>A personal touch</h3>
              <p style={styles.valueDesc}>
                We're engaged in the reseller community and take care of the people on our platform.
                Real support, from people who actually sell.
              </p>
            </div>
            <div style={styles.valueCard}>
              <div style={styles.valueIcon}><Users size={20} color="#fff" strokeWidth={2.25} /></div>
              <h3 style={styles.valueTitle}>Built by Sellers</h3>
              <p style={styles.valueDesc}>
                Every feature comes from firsthand experience running shows, shipping orders, and
                handling buyers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="ap-section" style={{ ...styles.section, backgroundColor: lp.offWhite }}>
        <div style={styles.sectionInner}>
          <h2 className="ap-section-title" style={styles.sectionTitle}>Get in touch</h2>
          <div className="ap-contact-grid" style={styles.contactGrid}>
            <a href="mailto:support@snagg.live" style={styles.contactCard}>
              <div style={styles.contactLabel}>General support</div>
              <div style={styles.contactEmail}>support@snagg.live</div>
            </a>
            <a href="mailto:privacy@snagg.live" style={styles.contactCard}>
              <div style={styles.contactLabel}>Privacy inquiries</div>
              <div style={styles.contactEmail}>privacy@snagg.live</div>
            </a>
          </div>
          <p style={styles.legalLine}>
            See our <Link to="/privacy" style={styles.link}>Privacy Policy</Link> and{' '}
            <Link to="/terms" style={styles.link}>Terms of Service</Link>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'DM Sans', system-ui, sans-serif",
    color: lp.midnightNavy,
    backgroundColor: lp.white,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
  },

  // Nav
  nav: {
    backgroundColor: lp.white,
    borderBottom: `1px solid ${lp.mist}`,
  },
  navContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    height: 64,
    display: 'flex',
    alignItems: 'center',
  },
  navLogoLink: {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  navLogoImg: {
    height: 32,
    width: 'auto',
    display: 'block',
  },

  // Hero
  hero: {
    backgroundColor: lp.offWhite,
    padding: '96px 0 64px',
    textAlign: 'center',
  },
  heroInner: {
    maxWidth: 820,
    margin: '0 auto',
    padding: '0 24px',
  },
  eyebrowPill: {
    display: 'inline-block',
    padding: '6px 20px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    background: lp.ctaGradient,
    borderRadius: 100,
    marginBottom: 24,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  heroHeadline: {
    fontFamily: "'BW Modelica', system-ui, sans-serif",
    fontSize: 56,
    fontWeight: 800,
    lineHeight: 1.1,
    marginBottom: 20,
    color: lp.midnightNavy,
  },
  heroHeadlineAccent: {
    background: `linear-gradient(135deg, var(--color-primary, #5F3DC4), var(--color-primary-light, #BCAEE6), var(--color-primary-rose, #CD3C9C))`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
  },
  heroSub: {
    fontSize: 20,
    lineHeight: 1.6,
    color: lp.mutedSlate,
    maxWidth: 640,
    margin: '0 auto',
  },

  // Sections
  section: {
    padding: '72px 0',
    textAlign: 'center',
  },
  sectionInner: {
    maxWidth: 1080,
    margin: '0 auto',
    padding: '0 24px',
  },
  sectionTitle: {
    fontFamily: "'BW Modelica', system-ui, sans-serif",
    fontSize: 32,
    fontWeight: 800,
    marginBottom: 16,
    color: lp.midnightNavy,
  },
  sectionBody: {
    fontSize: 17,
    lineHeight: 1.7,
    color: lp.mutedSlate,
    maxWidth: 720,
    margin: '0 auto',
  },

  // Values
  valuesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
    marginTop: 32,
    textAlign: 'left',
  },
  valueCard: {
    backgroundColor: lp.white,
    border: `1px solid ${lp.mist}`,
    borderRadius: 16,
    padding: 28,
  },
  valueIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: 12,
    background: lp.ctaGradient,
    marginBottom: 16,
  },
  valueTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 8,
    color: lp.midnightNavy,
    lineHeight: 1.3,
  },
  valueDesc: {
    fontSize: 15,
    lineHeight: 1.6,
    color: lp.mutedSlate,
  },

  // Contact cards
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 20,
    maxWidth: 720,
    margin: '32px auto 0',
  },
  contactCard: {
    display: 'block',
    backgroundColor: lp.white,
    border: `1px solid ${lp.mist}`,
    borderRadius: 16,
    padding: '24px 28px',
    textDecoration: 'none',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    textAlign: 'left',
  },
  contactLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: lp.mutedSlate,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  contactEmail: {
    fontSize: 18,
    fontWeight: 600,
    color: lp.iconPurple,
  },
  legalLine: {
    fontSize: 14,
    color: lp.mutedSlate,
    marginTop: 28,
  },
  link: {
    color: lp.iconPurple,
    textDecoration: 'none',
    fontWeight: 600,
  },
};
